import type {
  CollectionReference,
  DocumentReference,
  FieldValue,
} from 'firebase/firestore'
import {
  collection,
  doc,
  query,
  serverTimestamp,
  where,
  limit,
  and,
  or,
  deleteField,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { modelItemStream, modelListStream } from '../../firestore-mobx/stream'
import { partitionedQueryStream } from '../../firestore-mobx/stream'
import { convertDocumentSnapshotToModel } from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import type { FirestoreGlobalAuthor } from './schema'
import { globalAuthorSchema } from './schema'

function splitAuthorName(authorName: string): {
  firstName: string
  lastName: string
} {
  const parts = authorName.trim().split(/\s+/)
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' }
  }
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  }
}
import { GlobalAuthor } from '../../models/GlobalAuthor'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { safeDeleteStorageObject } from '../../util/safeDeleteStorageObject'
import {
  addDocWithError,
  getDocsWithError,
  updateDocWithError,
} from '../../firestore-mobx/fetch'
import { getDocWithError } from '../../firestore-mobx/fetch'
import { DocumentDoesNotExistError } from '../../types'

const converter: FirestoreDataConverter<FirestoreGlobalAuthor> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return globalAuthorSchema.parse(data)
  },
}

const getColRef = (
  firestore: Firestore
): CollectionReference<FirestoreGlobalAuthor> => {
  return collection(firestore, 'author').withConverter(converter)
}

const getDocRef = (
  firestore: Firestore,
  authorId: string
): DocumentReference<FirestoreGlobalAuthor> => {
  return doc(getColRef(firestore), authorId)
}

export const getGlobalAuthorList = (repository: FirebaseRepository) => {
  const ref = getColRef(repository.firestore)
  const q = query(ref)
  return modelListStream(repository, q, GlobalAuthor)
}

export const getGlobalAuthor = (
  repository: FirebaseRepository,
  { authorId }: { authorId: string }
) => {
  const ref = getDocRef(repository.firestore, authorId)
  return modelItemStream(repository, ref, GlobalAuthor)
}

export const getGlobalAuthors = (
  repository: FirebaseRepository,
  { authorIds }: { authorIds: string[] }
) => {
  const ref = getColRef(repository.firestore)
  return partitionedQueryStream({
    repository,
    ref,
    matchArray: authorIds.filter((id) => id),
    model: GlobalAuthor,
  })
}

export const fetchGlobalAuthor = async (
  repository: FirebaseRepository,
  {
    authorId,
    returnEmptyOnNotExists = false,
  }: { authorId: string; returnEmptyOnNotExists?: boolean }
) => {
  const docRef = getDocRef(repository.firestore, authorId)
  const docSnap = await getDocWithError(docRef, 'FetchGlobalAuthorError')

  if (!docSnap.exists()) {
    if (returnEmptyOnNotExists) return GlobalAuthor.empty(repository)
    throw new DocumentDoesNotExistError()
  }

  return convertDocumentSnapshotToModel(repository, docSnap, GlobalAuthor)
}

export const searchGlobalAuthors = async (
  repository: FirebaseRepository,
  { term }: { term: string }
) => {
  const safeTerm = term.trim().toLocaleLowerCase()
  const colRef = getColRef(repository.firestore)

  if (!safeTerm) {
    return []
  }

  const q = query(
    colRef,
    or(
      and(
        where('authorFirstNameLower', '>=', safeTerm),
        where('authorFirstNameLower', '<', safeTerm + '\uf8ff')
      ),
      and(
        where('authorLastNameLower', '>=', safeTerm),
        where('authorLastNameLower', '<', safeTerm + '\uf8ff')
      )
    ),
    limit(20)
  )

  const docs = await getDocsWithError(q, 'SearchGlobalAuthorsError')

  return docs.docs
    .map((docSnap) =>
      convertDocumentSnapshotToModel(repository, docSnap, GlobalAuthor)
    )
    .sort((a, b) => a.data.authorName.localeCompare(b.data.authorName))
}

export type GlobalAuthorFieldsForUpload = Pick<
  FirestoreGlobalAuthor,
  | 'authorBio'
  | 'authorImageURL'
  | 'authorImageFilename'
  | 'authorInstitution'
  | 'authorName'
  | 'authorTitle'
>

export const createGlobalAuthor = async (
  repository: FirebaseRepository,
  authorData: GlobalAuthorFieldsForUpload
) => {
  const colRef = getColRef(repository.firestore)

  interface WriteInterface extends Omit<
    FirestoreGlobalAuthor,
    'updatedAt' | 'createdAt'
  > {
    createdAt: FieldValue
    updatedAt: FieldValue
  }

  const { firstName, lastName } = splitAuthorName(authorData.authorName)

  const dataToWrite: WriteInterface = {
    ...authorData,
    authorNameLower: authorData.authorName.toLocaleLowerCase(),
    authorFirstNameLower: firstName.toLocaleLowerCase(),
    authorLastNameLower: lastName.toLocaleLowerCase(),
    authorInstitutionLower: authorData.authorInstitution.toLocaleLowerCase(),
    hidden: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  const newDoc = await addDocWithError(
    colRef,
    dataToWrite,
    'CreateGlobalAuthorError'
  )

  return newDoc.id
}

export const updateGlobalAuthor = async (
  repository: FirebaseRepository,
  authorId: string,
  authorData: GlobalAuthorFieldsForUpload
) => {
  const docRef = getDocRef(repository.firestore, authorId)

  const { firstName, lastName } = splitAuthorName(authorData.authorName)

  const dataToWrite = {
    ...authorData,
    authorNameLower: authorData.authorName.toLocaleLowerCase(),
    authorFirstNameLower: firstName.toLocaleLowerCase(),
    authorLastNameLower: lastName.toLocaleLowerCase(),
    authorInstitutionLower: authorData.authorInstitution.toLocaleLowerCase(),
    updatedAt: serverTimestamp(),
  }

  return await updateDocWithError(
    docRef,
    dataToWrite,
    'UpdateGlobalAuthorError'
  )
}

export const uploadGlobalAuthorImage = async (
  repository: FirebaseRepository,
  {
    authorId,
    file,
  }: {
    authorId: string
    file: File
  }
) => {
  const fileMimeType = file.type

  const storageRef = ref(repository.storage, `author/${authorId}/image.jpg`)

  await uploadBytes(storageRef, file, {
    contentType: fileMimeType,
  })

  // get the download url and strip the token param
  const downloadUrl = await getDownloadURL(storageRef)
  const downloadURI = new URL(downloadUrl)
  downloadURI.searchParams.delete('token')
  const urlWithoutToken = downloadURI.toString()

  const docRef = getDocRef(repository.firestore, authorId)

  await updateDocWithError(
    docRef,
    {
      authorImageURL: urlWithoutToken,
      authorImageFilename: file.name,
      updatedAt: serverTimestamp(),
    },
    'UploadGlobalAuthorImageError'
  )

  return urlWithoutToken
}

export const deleteGlobalAuthorImage = async (
  repository: FirebaseRepository,
  { authorId }: { authorId: string }
) => {
  const storageRef = ref(repository.storage, `author/${authorId}/image.jpg`)

  await safeDeleteStorageObject(storageRef)

  const docRef = getDocRef(repository.firestore, authorId)

  await updateDocWithError(
    docRef,
    {
      authorImageURL: deleteField(),
      authorImageFilename: deleteField(),
      updatedAt: serverTimestamp(),
    },
    'DeleteGlobalAuthorImageError'
  )
}

export const hideGlobalAuthor = async (
  repository: FirebaseRepository,
  authorId: string
) => {
  const docRef = getDocRef(repository.firestore, authorId)
  return await updateDocWithError(
    docRef,
    { hidden: true },
    'HideGlobalAuthorError'
  )
}

export const unhideGlobalAuthor = async (
  repository: FirebaseRepository,
  authorId: string
) => {
  const docRef = getDocRef(repository.firestore, authorId)
  return await updateDocWithError(
    docRef,
    { hidden: false },
    'UnhideGlobalAuthorError'
  )
}
